import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Calendar,
  Clock,
  Save,
  Plus,
  Trash2,
  Loader2,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Ban,
  MapPin,
  MessageSquare,
  Settings,
  Eye,
  Send,
  Trophy,
  Dumbbell,
} from 'lucide-react';
import GameSchedulePanel from './GameSchedulePanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchedulerPanel({ orgId, teams, userRole,
                                         sport = '', view = '', hideViewBar = false }) {
  // The Scheduler page wraps this panel in one tab PER SPORT and owns the
  // sub-tab bar, so it passes `sport` (2-char code), the active `view`, and
  // hideViewBar to suppress this component's own bar. Standalone callers pass
  // none of the three and keep the original self-contained behaviour.
  // See SCHEDULER_ROLE_PAGE.md.
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [seasonWindow, setSeasonWindow] = useState({ first: '', last: '' });
  // scheduleMode: 'game' | 'practice'. Drives which endpoints
  // GameSchedulePanel hits (game-schedule vs practice-schedule) and
  // whether WP publish is offered. Persists per browser via localStorage
  // so the AD's last choice sticks across page loads.
  const [scheduleMode, setScheduleMode] = useState(() => {
    try { return localStorage.getItem('ol.scheduleMode') || 'game'; } catch { return 'game'; }
  });
  useEffect(() => {
    try { localStorage.setItem('ol.scheduleMode', scheduleMode); } catch {}
  }, [scheduleMode]);
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [availableSlots, setAvailableSlots] = useState(null);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI state
  const [ownView, setOwnView] = useState('schedule');
  const activeView = view || ownView;        // parent-driven when provided
  const setActiveView = view ? () => {} : setOwnView;
  const [selectedDate, setSelectedDate] = useState(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [responseReason, setResponseReason] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Block form
  const [blockForm, setBlockForm] = useState({ date: '', start_time: '', end_time: '', reason: '', all_day: true });

  // Config form
  const [configForm, setConfigForm] = useState({ season_start: '', season_end: '', time_allocations: [] });

  const isScheduler = ['scheduler', 'ad', 'company_support'].includes(userRole);

  const bg = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const subtle = isDark ? 'bg-slate-700/30' : 'bg-slate-50';

  const fetchData = useCallback(async () => {
    if (!orgId) return;
    try {
      const [configRes, calRes, blockedRes, interestsRes] = await Promise.all([
        axios.get(`${API_URL}/api/scheduler/config/${orgId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/scheduler/calendar/${orgId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/scheduler/blocked-slots/${orgId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/scheduler/interests/${orgId}`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setConfig(configRes.data);
      setCalendarEntries(calRes.data.entries || []);
      setBlockedSlots(blockedRes.data || []);
      setInterests(interestsRes.data || []);
      if (configRes.data) {
        setConfigForm({
          season_start: configRes.data.season_start || '',
          season_end: configRes.data.season_end || '',
          time_allocations: configRes.data.time_allocations || [],
        });
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const days = [];
    const startDay = first.getDay();
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
    return days;
  };

  const getEntriesForDate = (dateStr) => calendarEntries.filter(e => e.date === dateStr);

  const formatDateStr = (d) => {
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Actions
  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/scheduler/config/${orgId}`, configForm, { withCredentials: true });
      setShowConfigDialog(false);
      fetchData();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const addBlock = async () => {
    setSaving(true);
    try {
      const payload = {
        date: blockForm.date,
        reason: blockForm.reason,
      };
      if (!blockForm.all_day) {
        payload.start_time = blockForm.start_time;
        payload.end_time = blockForm.end_time;
      }
      await axios.post(`${API_URL}/api/scheduler/blocked-slots/${orgId}`, payload, { withCredentials: true });
      setShowBlockDialog(false);
      setBlockForm({ date: '', start_time: '', end_time: '', reason: '', all_day: true });
      fetchData();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const removeBlock = async (slotId) => {
    try {
      await axios.delete(`${API_URL}/api/scheduler/blocked-slots/${slotId}`, { withCredentials: true });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const respondToGame = async (action) => {
    if (!selectedGame) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/scheduler/respond-game/${selectedGame.id}`, {
        action, reason: responseReason,
      }, { withCredentials: true });
      setShowResponseDialog(false);
      setSelectedGame(null);
      setResponseReason('');
      fetchData();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const approveInterest = async (interestId) => {
    try {
      await axios.post(`${API_URL}/api/scheduler/interests/${interestId}/approve`, {}, { withCredentials: true });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const addTimeAllocation = () => {
    setConfigForm({ ...configForm, time_allocations: [...configForm.time_allocations, { level: '', sport: 'Basketball', duration_minutes: 90 }] });
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  const days = getDaysInMonth(calendarMonth);
  const monthStr = calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const pendingGames = calendarEntries.filter(e => e.type === 'game' && e.status === 'pending');
  const pendingInterests = interests.filter(i => i.status === 'pending');

  return (
    <div className="space-y-4" data-testid="scheduler-panel">
      {/* Header — hidden on Schedule view because its own sticky toolbar
          already carries the sport / publish / add / settings controls. */}
      {activeView !== 'schedule' && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-500" />
            <h2 className={`text-lg font-semibold ${text}`}>Scheduling Manager</h2>
            {pendingGames.length > 0 && (
              <Badge className="bg-amber-600 text-white">{pendingGames.length} pending</Badge>
            )}
          </div>
          {isScheduler && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowConfigDialog(true)}
                className={isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : ''}
                data-testid="scheduler-config-btn"
              >
                <Settings className="h-4 w-4 mr-1" /> Configure
              </Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => { setBlockForm({ ...blockForm, date: formatDateStr(new Date()) }); setShowBlockDialog(true); }}
                data-testid="block-time-btn"
              >
                <Ban className="h-4 w-4 mr-1" /> Block Time
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Season info — shown only when NOT on Game Schedule view; the Game
          Schedule toolbar already exposes the season via its own selector,
          so re-rendering this bar there is redundant and pushes the grid too
          far down. */}
      {activeView !== 'schedule' && (seasonWindow.first || config?.season_start) && (
        <div className={`flex items-center gap-4 p-3 rounded-lg ${subtle}`}>
          <span className={`text-xs font-medium ${muted}`}>Season Window:</span>
          <span className={`text-sm font-medium ${text}`}>{seasonWindow.first || config.season_start} to {seasonWindow.last || config.season_end}</span>
          {config.time_allocations?.length > 0 && (
            <>
              <span className={`text-xs ${muted}`}>|</span>
              {config.time_allocations.map((ta, i) => (
                <Badge key={i} variant="outline" className="text-xs">{ta.level}: {ta.duration_minutes}min</Badge>
              ))}
            </>
          )}
        </div>
      )}

      {/* View tabs — compact on Schedule view so the grid doesn't get pushed
          below the fold. */}
      {!hideViewBar && (
      <div className={`flex gap-1 flex-wrap ${activeView === 'schedule' ? '' : ''}`}>
        {['schedule', 'calendar', 'pending', 'blocked', 'interests'].map(v => (
          <Button key={v} size="sm" variant={activeView === v ? 'default' : 'ghost'}
            className={activeView === v ? (v === 'schedule' ? 'bg-amber-600 text-white h-7 text-xs' : 'bg-orange-600 text-white h-7 text-xs') : `${muted} h-7 text-xs`}
            onClick={() => setActiveView(v)} data-testid={`view-${v}-btn`}
          >
            {v === 'schedule' ? <><Trophy className="h-3 w-3 mr-1" />Schedule</> : v === 'calendar' ? 'Calendar' : v === 'pending' ? `Requests (${pendingGames.length})` : v === 'blocked' ? `Blocked (${blockedSlots.length})` : `Interest (${pendingInterests.length})`}
          </Button>
        ))}
      </div>
      )}

      {/* Schedule mode toggle — Game vs Practice. Renders only on the
          Schedule and Calendar sub-views (the other views are
          mode-agnostic). Practices live in their own collection
          (practice_slots) and never publish to WordPress. */}
      {(activeView === 'schedule' || activeView === 'calendar') && (
        <div className={`flex gap-1 items-center pl-1`} data-testid="schedule-mode-toggle">
          <span className={`text-[11px] ${muted} mr-2`}>Show:</span>
          <Button size="sm" variant={scheduleMode === 'game' ? 'default' : 'ghost'}
            className={scheduleMode === 'game' ? 'bg-amber-600 text-white h-7 text-xs' : `${muted} h-7 text-xs`}
            onClick={() => setScheduleMode('game')} data-testid="mode-game-btn">
            <Trophy className="h-3 w-3 mr-1" />Games
          </Button>
          <Button size="sm" variant={scheduleMode === 'practice' ? 'default' : 'ghost'}
            className={scheduleMode === 'practice' ? 'bg-sky-600 text-white h-7 text-xs' : `${muted} h-7 text-xs`}
            onClick={() => setScheduleMode('practice')} data-testid="mode-practice-btn">
            <Dumbbell className="h-3 w-3 mr-1" />Practices
          </Button>
        </div>
      )}

      {/* GAME / PRACTICE SCHEDULE VIEW — single component, mode prop
          drives endpoints + WP publish behaviour. */}
      {activeView === 'schedule' && (
        <GameSchedulePanel
          orgId={orgId} onSeasonWindow={setSeasonWindow}
          mode={scheduleMode} lockedSport={sport}
        />
      )}

      {/* CALENDAR VIEW — same component, view=calendar. Mode toggle
          above filters game vs practice events. */}
      {activeView === 'calendar' && (
        <GameSchedulePanel
          orgId={orgId} onSeasonWindow={setSeasonWindow}
          initialView="calendar" mode={scheduleMode} lockedSport={sport}
        />
      )}

      {/* Legacy calendar (kept behind a disabled flag in case we need it) */}
      {false && activeView === 'calendar' && (
        <Card className={bg}>
          <CardContent className="pt-4">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button size="sm" variant="ghost" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className={`text-base font-semibold ${text}`}>{monthStr}</span>
              <Button size="sm" variant="ghost" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-px mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className={`text-center text-xs font-medium py-1 ${muted}`}>{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-px" data-testid="scheduler-calendar-grid">
              {days.map((day, idx) => {
                if (!day) return <div key={`empty-${idx}`} className="h-20" />;
                const dateStr = formatDateStr(day);
                const entries = getEntriesForDate(dateStr);
                const hasBlocked = entries.some(e => e.type === 'blocked');
                const hasGame = entries.some(e => e.type === 'game');
                const hasPending = entries.some(e => e.type === 'game' && e.status === 'pending');
                const isToday = dateStr === formatDateStr(new Date());
                const isSelected = selectedDate === dateStr;

                return (
                  <div
                    key={dateStr}
                    className={`h-20 p-1 border rounded cursor-pointer transition-colors
                      ${isDark ? 'border-slate-700 hover:border-slate-500' : 'border-slate-200 hover:border-slate-400'}
                      ${isToday ? (isDark ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-300') : ''}
                      ${isSelected ? (isDark ? 'bg-orange-900/20 border-orange-500' : 'bg-orange-50 border-orange-300') : ''}
                      ${hasBlocked ? (isDark ? 'bg-red-900/10' : 'bg-red-50') : ''}
                    `}
                    onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                    data-testid={`cal-day-${dateStr}`}
                  >
                    <div className={`text-xs font-medium ${isToday ? 'text-blue-500' : text}`}>{day.getDate()}</div>
                    <div className="flex flex-wrap gap-0.5 mt-0.5">
                      {hasBlocked && <div className="w-2 h-2 rounded-full bg-red-500" title="Blocked" />}
                      {hasGame && !hasPending && <div className="w-2 h-2 rounded-full bg-green-500" title="Accepted" />}
                      {hasPending && <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" title="Pending" />}
                    </div>
                    {entries.slice(0, 2).map(e => (
                      <div key={e.id} className={`text-[10px] truncate leading-tight mt-0.5 ${e.type === 'blocked' ? 'text-red-400' : e.status === 'pending' ? 'text-amber-400' : 'text-green-400'}`}>
                        {e.type === 'blocked' ? `${e.start_time} Blocked` : `${e.start_time} vs ${e.opponent_name}`}
                      </div>
                    ))}
                    {entries.length > 2 && <div className={`text-[9px] ${muted}`}>+{entries.length - 2} more</div>}
                  </div>
                );
              })}
            </div>

            {/* Selected date detail */}
            {selectedDate && (
              <div className={`mt-4 p-3 rounded-lg border ${isDark ? 'border-slate-600 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-medium text-sm ${text}`}>{selectedDate}</span>
                  {isScheduler && (
                    <Button size="sm" variant="outline" className="text-xs"
                      onClick={() => { setBlockForm({ ...blockForm, date: selectedDate }); setShowBlockDialog(true); }}>
                      <Ban className="h-3 w-3 mr-1" /> Block This Date
                    </Button>
                  )}
                </div>
                {getEntriesForDate(selectedDate).length === 0 ? (
                  <p className={`text-xs ${muted}`}>No events scheduled</p>
                ) : (
                  <div className="space-y-2">
                    {getEntriesForDate(selectedDate).map(e => (
                      <div key={e.id} className={`flex items-center justify-between p-2 rounded ${isDark ? 'bg-slate-700/50' : 'bg-white'}`}>
                        <div className="flex items-center gap-2">
                          {e.type === 'blocked' ? (
                            <Badge className="bg-red-600 text-xs">Blocked</Badge>
                          ) : (
                            <Badge className={`text-xs ${e.status === 'pending' ? 'bg-amber-600' : e.status === 'accepted' ? 'bg-green-600' : 'bg-red-600'}`}>
                              {e.status}
                            </Badge>
                          )}
                          <span className={`text-sm ${text}`}>
                            {e.type === 'blocked' ? `${e.start_time}-${e.end_time} ${e.reason}` : `${e.start_time} — vs ${e.opponent_name}`}
                          </span>
                          {e.venue_name && <span className={`text-xs ${muted}`}><MapPin className="h-3 w-3 inline" /> {e.venue_name}</span>}
                        </div>
                        <div className="flex gap-1">
                          {e.type === 'blocked' && isScheduler && (
                            <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => removeBlock(e.id)}><Trash2 className="h-3 w-3" /></Button>
                          )}
                          {e.type === 'game' && e.status === 'pending' && isScheduler && (
                            <Button size="sm" className="bg-amber-600 h-7 text-xs" onClick={() => { setSelectedGame(e); setShowResponseDialog(true); }}>
                              Review
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PENDING REQUESTS VIEW */}
      {activeView === 'pending' && (
        <Card className={bg}>
          <CardHeader className="pb-2">
            <CardTitle className={`text-sm font-medium ${muted}`}>Pending Game Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {pendingGames.length === 0 ? (
              <p className={`text-sm text-center py-6 ${muted}`}>No pending game requests</p>
            ) : (
              <div className="space-y-3">
                {pendingGames.map(g => (
                  <div key={g.id} className={`p-3 rounded-lg border ${isDark ? 'border-slate-600 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm font-medium ${text}`}>
                          vs {g.opponent_name} — {g.date} at {g.start_time}
                        </p>
                        <p className={`text-xs ${muted}`}>
                          {g.venue_name && <><MapPin className="h-3 w-3 inline" /> {g.venue_name} — </>}
                          {g.is_home ? 'Home' : 'Away'} {g.duration_minutes && `— ${g.duration_minutes}min`}
                        </p>
                        {g.message && <p className={`text-xs mt-1 italic ${muted}`}><MessageSquare className="h-3 w-3 inline" /> "{g.message}"</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8"
                          onClick={() => { setSelectedGame(g); setResponseReason(''); setShowResponseDialog(true); }}
                          data-testid={`review-game-${g.id}`}
                        >
                          <Eye className="h-3 w-3 mr-1" /> Review
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* BLOCKED SLOTS VIEW */}
      {activeView === 'blocked' && (
        <Card className={bg}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className={`text-sm font-medium ${muted}`}>Blocked Time Slots</CardTitle>
            {isScheduler && (
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => { setBlockForm({ ...blockForm, date: formatDateStr(new Date()) }); setShowBlockDialog(true); }}>
                <Plus className="h-4 w-4 mr-1" /> Add Block
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {blockedSlots.length === 0 ? (
              <p className={`text-sm text-center py-6 ${muted}`}>No blocked time slots</p>
            ) : (
              <div className="space-y-2">
                {blockedSlots.map(b => (
                  <div key={b.id} className={`flex items-center justify-between p-2 rounded-lg ${subtle}`}>
                    <div>
                      <p className={`text-sm font-medium ${text}`}>{b.date} {b.all_day ? '(All Day)' : `${b.start_time} - ${b.end_time}`}</p>
                      {b.reason && <p className={`text-xs ${muted}`}>{b.reason}</p>}
                    </div>
                    {isScheduler && (
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeBlock(b.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* INTERESTS VIEW */}
      {activeView === 'interests' && (

        <Card className={bg}>
          <CardHeader className="pb-2">
            <CardTitle className={`text-sm font-medium ${muted}`}>Teams Requesting to Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            {pendingInterests.length === 0 ? (
              <p className={`text-sm text-center py-6 ${muted}`}>No pending interest requests</p>
            ) : (
              <div className="space-y-2">
                {pendingInterests.map(i => (
                  <div key={i.id} className={`flex items-center justify-between p-3 rounded-lg ${subtle}`}>
                    <div>
                      <p className={`text-sm font-medium ${text}`}>{i.requesting_org_name} — {i.team_name}</p>
                      <p className={`text-xs ${muted}`}>{i.contact_name} ({i.contact_email})</p>
                      {i.message && <p className={`text-xs italic mt-1 ${muted}`}>"{i.message}"</p>}
                    </div>
                    {isScheduler && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => approveInterest(i.id)}
                        data-testid={`approve-interest-${i.id}`}
                      >
                        <Check className="h-3 w-3 mr-1" /> Approve
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CONFIG DIALOG */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={text}>Scheduler Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="scheduler-config-dialog">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={`text-xs ${muted}`}>Season Start Date</Label>
                <Input type="date" value={configForm.season_start}
                  onChange={e => setConfigForm({ ...configForm, season_start: e.target.value })}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="season-start" />
              </div>
              <div>
                <Label className={`text-xs ${muted}`}>Season End Date</Label>
                <Input type="date" value={configForm.season_end}
                  onChange={e => setConfigForm({ ...configForm, season_end: e.target.value })}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="season-end" />
              </div>
            </div>
            <div>
              <Label className={`text-xs ${muted}`}>Time Allocation per Level (game block duration)</Label>
              {configForm.time_allocations.map((ta, idx) => (
                <div key={idx} className="flex items-end gap-2 mt-2">
                  <div className="flex-1">
                    <Input value={ta.level} placeholder="Level (MS, JV, Varsity)"
                      onChange={e => { const u = [...configForm.time_allocations]; u[idx] = { ...u[idx], level: e.target.value }; setConfigForm({ ...configForm, time_allocations: u }); }}
                      className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
                  </div>
                  <div className="w-24">
                    <Input type="number" value={ta.duration_minutes} min={30} max={240}
                      onChange={e => { const u = [...configForm.time_allocations]; u[idx] = { ...u[idx], duration_minutes: parseInt(e.target.value) || 90 }; setConfigForm({ ...configForm, time_allocations: u }); }}
                      className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
                  </div>
                  <span className={`text-xs ${muted} mb-2`}>min</span>
                  <Button size="sm" variant="ghost" className="text-red-400 mb-1"
                    onClick={() => setConfigForm({ ...configForm, time_allocations: configForm.time_allocations.filter((_, i) => i !== idx) })}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="mt-2" onClick={addTimeAllocation}>
                <Plus className="h-3 w-3 mr-1" /> Add Level
              </Button>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowConfigDialog(false)} className={muted}>Cancel</Button>
              <Button onClick={saveConfig} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-config-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* BLOCK TIME DIALOG */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle className={text}>Block Time Slot</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="block-dialog">
            <div>
              <Label className={`text-xs ${muted}`}>Date</Label>
              <Input type="date" value={blockForm.date}
                onChange={e => setBlockForm({ ...blockForm, date: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="block-date" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="all-day" checked={blockForm.all_day}
                onChange={e => setBlockForm({ ...blockForm, all_day: e.target.checked })} />
              <Label htmlFor="all-day" className={isDark ? 'text-slate-300' : ''}>Block entire day</Label>
            </div>
            {!blockForm.all_day && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label className={`text-xs ${muted}`}>Start Time</Label>
                  <Input type="time" value={blockForm.start_time}
                    onChange={e => setBlockForm({ ...blockForm, start_time: e.target.value })}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} /></div>
                <div><Label className={`text-xs ${muted}`}>End Time</Label>
                  <Input type="time" value={blockForm.end_time}
                    onChange={e => setBlockForm({ ...blockForm, end_time: e.target.value })}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} /></div>
              </div>
            )}
            <div>
              <Label className={`text-xs ${muted}`}>Reason (optional)</Label>
              <Input value={blockForm.reason}
                onChange={e => setBlockForm({ ...blockForm, reason: e.target.value })}
                placeholder="Holiday, Maintenance, etc."
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="block-reason" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowBlockDialog(false)} className={muted}>Cancel</Button>
              <Button onClick={addBlock} disabled={!blockForm.date || saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-block-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Ban className="h-4 w-4 mr-1" /> Block</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* RESPONSE DIALOG (accept/reject) */}
      <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle className={text}>Review Game Request</DialogTitle>
          </DialogHeader>
          {selectedGame && (
            <div className="space-y-3" data-testid="response-dialog">
              <div className={`p-3 rounded-lg ${subtle}`}>
                <p className={`text-sm font-medium ${text}`}>vs {selectedGame.opponent_name}</p>
                <p className={`text-xs ${muted}`}>{selectedGame.date} at {selectedGame.start_time} — {selectedGame.is_home ? 'Home' : 'Away'}</p>
                {selectedGame.venue_name && <p className={`text-xs ${muted}`}><MapPin className="h-3 w-3 inline" /> {selectedGame.venue_name}</p>}
                {selectedGame.message && <p className={`text-xs italic mt-2 ${muted}`}><MessageSquare className="h-3 w-3 inline" /> "{selectedGame.message}"</p>}
              </div>
              <div>
                <Label className={`text-xs ${muted}`}>Message to opponent (required for reject)</Label>
                <textarea value={responseReason}
                  onChange={e => setResponseReason(e.target.value)}
                  className={`w-full h-20 rounded-md px-3 py-2 border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  placeholder="Enter reason for rejection, or a message for acceptance..."
                  data-testid="response-reason" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowResponseDialog(false)} className={muted}>Cancel</Button>
                <Button onClick={() => respondToGame('reject')} disabled={saving}
                  className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-game-btn">
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
                <Button onClick={() => respondToGame('accept')} disabled={saving}
                  className="bg-green-600 hover:bg-green-700 text-white" data-testid="accept-game-btn">
                  <Check className="h-4 w-4 mr-1" /> Accept
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
